/**
 * Таблица маршрутов. Только форма — ни одного решения о доступе здесь нет:
 * маршрут лишь *объявляет* требования (`meta.public`, `meta.systemRoles`,
 * `meta.eventManage`), а исполняет их `guards.ts`.
 *
 * `meta` типизирована в `types/router.d.ts`, поэтому опечатка в ключе
 * (`role` вместо `systemRoles`) ломает компиляцию, а не превращается в
 * открытый маршрут в рантайме.
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  ФОРМА URL: МЕРОПРИЯТИЕ — ЭТО ПРЕФИКС, А НЕ ФИЛЬТР
 * ───────────────────────────────────────────────────────────────────────────
 *
 *     /events/:eventId/participants
 *     /events/:eventId/fields
 *     /events/:eventId/roles
 *     /events/:eventId/members
 *
 * Так же, как на бэкенде, где `event_id` — часть пути, а не параметр запроса.
 * Причина не в симметрии: скоуп в пути означает, что ссылку на список
 * участников можно переслать, и она откроется в том же соревновании. Скоуп в
 * query (`/participants?event=…`) первым же копипастом теряется, и человек
 * смотрит на чужие данные, будучи уверен, что смотрит на свои.
 *
 * Все эти маршруты — дети `EventLayout`, который грузит мероприятие, состав и
 * оба справочника **один раз** и держит их, пока человек внутри соревнования.
 */
import type { RouteRecordRaw } from 'vue-router'
import type { SystemRole } from '@/types/api'
import AppShell from '@/components/layout/AppShell.vue'
import EventLayout from '@/components/layout/EventLayout.vue'
import ScannerLayout from '@/components/layout/ScannerLayout.vue'

/**
 * Единственное место, где перечислены «только для администратора» маршруты.
 * `satisfies` ловит опечатку в имени роли на компиляции; `as const` даёт
 * `readonly`, который и ждёт `RouteMeta['systemRoles']`.
 */
const ADMIN_ONLY = ['SYSTEM_ROLE_ADMIN'] as const satisfies readonly SystemRole[]

/**
 * Layout'ы импортируются **не лениво**, в отличие от вью.
 *
 * Они нужны на каждом приватном маршруте, и ленивая загрузка дала бы лишний
 * round-trip ровно в тот момент, когда пользователь и так ждёт `bootstrap()`.
 * Вью — наоборот, все до одной ленивые: экран пользователей и экран импорта
 * никогда не нужны одновременно.
 */
export const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { public: true, title: 'Вход' },
  },

  {
    path: '/',
    component: AppShell,
    children: [
      /**
       * Корень — это не экран, а решение «куда по умолчанию».
       *
       * Именно мероприятия, а не пользователи: список учётных записей доступен
       * только администратору, и обычный пользователь получал бы редирект на
       * `/forbidden` при каждом заходе на корень.
       */
      { path: '', redirect: { name: 'events' } },

      /* ───────────────────────── МЕРОПРИЯТИЯ ───────────────────────── */

      {
        path: 'events',
        name: 'events',
        component: () => import('@/views/events/EventsListView.vue'),
        meta: { title: 'Мероприятия' },
      },

      /**
       * ⚠️ ПОРЯДОК ЗНАЧИМ: `events/new` объявляется **до** `events/:eventId`.
       *
       * vue-router сортирует маршруты одной глубины так, что статический
       * сегмент выигрывает у параметра, но полагаться на это при равном счёте
       * нельзя — при перестановке `/events/new` попыталось бы загрузить
       * мероприятие с id `new` и получило бы `VALIDATION_FAILED` на ровном
       * месте. Не менять местами.
       *
       * Создавать может любой вошедший: администратор получает мероприятие без
       * владельцев, пользователь — становится его владельцем сам (сервер
       * добавляет его в состав той же транзакцией). Удалять — только админ.
       */
      {
        path: 'events/new',
        name: 'event-create',
        component: () => import('@/views/events/EventCreateView.vue'),
        meta: { title: 'Новое мероприятие' },
      },

      /* ───────────────────── СКОУП ОДНОГО МЕРОПРИЯТИЯ ──────────────── */

      {
        path: 'events/:eventId',
        component: EventLayout,
        // `props: true` на детях: вью получают `eventId` пропом и не лезут в
        // `useRoute()` — так их можно смонтировать в тесте без роутера.
        props: true,
        children: [
          { path: '', redirect: { name: 'event-participants' } },

          {
            path: 'participants',
            name: 'event-participants',
            component: () => import('@/views/participants/ParticipantsListView.vue'),
            props: true,
            meta: { title: 'Участники' },
          },
          /**
           * ⚠️ И здесь порядок значим, причём литеральных сегментов два:
           * `participants/new` и `participants/import` — до `participants/:id`.
           */
          {
            path: 'participants/new',
            name: 'participant-create',
            component: () => import('@/views/participants/ParticipantCreateView.vue'),
            props: true,
            meta: { eventManage: true, title: 'Новый участник' },
          },
          {
            path: 'participants/import',
            name: 'participant-import',
            component: () => import('@/views/participants/ParticipantImportView.vue'),
            props: true,
            meta: { eventManage: true, title: 'Импорт участников' },
          },
          {
            path: 'participants/:id',
            name: 'participant-edit',
            component: () => import('@/views/participants/ParticipantEditView.vue'),
            props: true,
            // Без `eventManage`: карточку участника наблюдателю видеть можно —
            // именно её открывают после сканирования QR. Форма внутри неё сама
            // становится доступной только для чтения.
            meta: { title: 'Участник' },
          },

          {
            path: 'settings',
            name: 'event-settings',
            component: () => import('@/views/events/EventSettingsView.vue'),
            props: true,
            meta: { eventManage: true, title: 'Настройки мероприятия' },
          },
          {
            path: 'members',
            name: 'event-members',
            component: () => import('@/views/events/EventMembersView.vue'),
            props: true,
            // Без `eventManage`: состав виден любому участнику — знать, к кому
            // идти за доступом, это чтение. Кнопки внутри закрыты `canManage`.
            meta: { title: 'Состав мероприятия' },
          },
          /**
           * Роли участников — список, и их дополнительные поля.
           *
           * `/fields` больше нет: поле привязано к роли, и отдельный список
           * полей не мог ответить на вопрос, ради которого его открывают —
           * кого это поле касается. Старый адрес намеренно не редиректится:
           * раздел существовал недолго, а лишний редирект живёт вечно.
           *
           * Общие поля (те, что применяются ко ВСЕМ, включая безролевых
           * участников) остаются на этом экране: страницы роли у них нет.
           * Поля конкретной роли живут на её странице.
           */
          {
            path: 'roles',
            name: 'event-participant-roles',
            component: () => import('@/views/events/ParticipantRolesView.vue'),
            props: true,
            meta: { title: 'Роли и дополнительные поля' },
          },
          /**
           * ⚠️ И здесь порядок значим: `roles/new` — до `roles/:roleId`.
           * См. комментарий у `events/new`; при перестановке страница создания
           * попыталась бы найти роль с id `new` и показала бы «роль не найдена».
           *
           * `eventManage` — как у `participants/new`, а НЕ как у списка ролей:
           * страница создания целиком состоит из формы, которую наблюдателю
           * нечем отправить. Кнопка «Создать роль» в списке и так скрыта под
           * `canManage`, так что мёртвой ссылки этот гвард не создаёт.
           */
          {
            path: 'roles/new',
            name: 'event-participant-role-create',
            component: () => import('@/views/events/ParticipantRoleCreateView.vue'),
            props: true,
            meta: { eventManage: true, title: 'Новая роль участника' },
          },
          /**
           * Карточка роли: название, цвет, зоны доступа и поля формы этой роли.
           *
           * Без `eventManage`, ровно как список ролей, состав и зоны, — и это
           * не послабление: вопрос «а раздевалки спортсмену дали?» задаёт
           * оператор аккредитации, который наблюдатель. Правки внутри закрыты
           * `canManage`, а последнее слово в любом случае за сервером.
           */
          {
            path: 'roles/:roleId',
            name: 'event-participant-role-edit',
            component: () => import('@/views/events/ParticipantRoleEditView.vue'),
            props: true,
            meta: { title: 'Роль участника' },
          },
          /**
           * Зоны доступа — участки ПЛОЩАДКИ, а не права в системе.
           *
           * Без `eventManage`, как и состав: справочник видит любой участник —
           * знать, что означает `LASK` на бейдже, это чтение. Кнопки внутри
           * закрыты `canManage`.
           */
          {
            path: 'access-zones',
            name: 'event-access-zones',
            component: () => import('@/views/events/AccessZonesView.vue'),
            props: true,
            meta: { title: 'Зоны доступа' },
          },
          /**
           * Уровни доступа — цифра на бейдже, а не ступень полномочий.
           *
           * ⚠️ ВТОРОЙ справочник рядом с зонами, а не замена им: зона говорит,
           * куда пускают, уровень — насколько глубоко. Роль выдаёт оба набора
           * независимо, поэтому и маршрута два.
           *
           * Без `eventManage`, ровно как зоны: знать, что означает цифра на
           * бейдже, — это чтение. Кнопки внутри закрыты `canManage`.
           */
          {
            path: 'access-levels',
            name: 'event-access-levels',
            component: () => import('@/views/events/AccessLevelsView.vue'),
            props: true,
            meta: { title: 'Уровни доступа' },
          },
          /**
           * Справочник пиктограмм.
           *
           * Без `eventManage`, как зоны и уровни: знать, что за картинка на
           * бейдже, — это чтение. Кнопки внутри закрыты `canManage`.
           */
          {
            path: 'pictograms',
            name: 'event-pictograms',
            component: () => import('@/views/events/PictogramsView.vue'),
            props: true,
            meta: { title: 'Пиктограммы' },
          },
          /**
           * Конструктор печатного бейджа.
           *
           * Без `eventManage`, как состав и зоны, и это НЕ послабление: макет
           * читает оператор аккредитации, чтобы печатать, а он наблюдатель.
           * Закрыв маршрут правами на запись, мы отрезали бы от печати ровно
           * тех, для кого она существует. Кнопки внутри закрыты `canManage`.
           */
          {
            path: 'badge',
            name: 'event-badge',
            component: () => import('@/views/events/BadgeTemplateView.vue'),
            props: true,
            meta: { title: 'Бейдж' },
          },
        ],
      },

      /* ───────────────────────── ПОЛЬЗОВАТЕЛИ ──────────────────────── */

      /**
       * Весь раздел — только администратор, **включая чтение**: список учётных
       * записей это системные данные, а `CreateUser` и `SetUserRole` раздают
       * системную роль.
       */
      {
        path: 'users',
        name: 'users',
        component: () => import('@/views/users/UsersListView.vue'),
        meta: { systemRoles: ADMIN_ONLY, title: 'Пользователи' },
      },
      {
        // ⚠️ До `users/:id` — см. комментарий у `events/new`.
        path: 'users/new',
        name: 'user-create',
        component: () => import('@/views/users/UserCreateView.vue'),
        meta: { systemRoles: ADMIN_ONLY, title: 'Новый пользователь' },
      },
      {
        path: 'users/:id',
        name: 'user-edit',
        component: () => import('@/views/users/UserEditView.vue'),
        props: true,
        meta: { systemRoles: ADMIN_ONLY, title: 'Пользователь' },
      },

      /* ───────────────────────── СПРАВОЧНИК СТРАН ──────────────────── */

      /**
       * Страны — единственный справочник БЕЗ мероприятия в адресе, поэтому он
       * и стоит здесь, рядом с пользователями, а не внутри `events/:eventId`.
       * Роли и зоны локальны для соревнования; флаг Беларуси одинаков везде, и
       * копия справочника на мероприятие означала бы 249 строк, заведённых
       * заново каждым организатором.
       *
       * ⚠️ `systemRoles: ADMIN_ONLY` — про ЗАПИСЬ, а не про чтение: список
       * читает любой аутентифицированный (он нужен карточке участника и печати
       * бейджа). Но экран целиком состоит из правок, и наблюдателю здесь нечего
       * делать, кроме как получить 403 на первом же нажатии. Смотреть страны
       * он будет там, где они ему нужны, — в карточке и на бейдже.
       *
       * Страницы создания страны нет намеренно: ручки создания нет в API.
       */
      {
        path: 'countries',
        name: 'countries',
        component: () => import('@/views/countries/CountriesView.vue'),
        meta: { systemRoles: ADMIN_ONLY, title: 'Страны' },
      },

      /* ─────────────────────────── АККАУНТ ─────────────────────────── */

      {
        path: 'account',
        name: 'profile',
        component: () => import('@/views/account/ProfileView.vue'),
        meta: { title: 'Мой профиль' },
      },
      {
        path: 'account/password',
        name: 'change-password',
        component: () => import('@/views/account/ChangePasswordView.vue'),
        meta: { title: 'Смена пароля' },
      },
      {
        path: 'account/sessions',
        name: 'sessions',
        component: () => import('@/views/account/SessionsView.vue'),
        meta: { title: 'Активные сессии' },
      },

      /**
       * 403 живёт **внутри** shell'а и **не** публичен: сюда попадает только
       * вошедший пользователь, которому не хватило роли. Анонима на
       * `/forbidden` гвард отправит на логин — «доступ запрещён» без
       * объяснения, кому именно, это худший из возможных ответов.
       */
      {
        path: 'forbidden',
        name: 'forbidden',
        component: () => import('@/views/errors/ForbiddenView.vue'),
        meta: { title: 'Доступ запрещён' },
      },
    ],
  },

  /**
   * ─────────────────────────── СКАНЕР ───────────────────────────
   *
   * Отдельная ветка **вне** `AppShell`, и это про устройство, а не про
   * оформление: сканер работает на телефоне в руке у человека на входе, где
   * сайдбар админки съедает половину экрана и добавляет полтора десятка
   * мишеней, по которым можно промахнуться посреди очереди.
   *
   * Авторизация та же самая: те же токены, тот же гвард. Разделены только
   * маршруты и каркас.
   *
   * ⚠️ `meta.eventManage` здесь НЕ ставится, и это обязательное условие
   * работоспособности: `ResolveQR` требует только доступа на чтение, то есть
   * наблюдатель («оператор аккредитации») сканировать может и должен. Закрыв
   * маршрут правами на запись, мы отрезали бы от сканера ровно тех, для кого
   * он сделан. Простановку отметок ограничивает сама карточка результата.
   */
  /**
   * Вход по одноразовому коду. ПУБЛИЧНЫЙ и **вне** `ScannerLayout`.
   *
   * Публичный по необходимости: сюда попадают, отсканировав QR владельца, и
   * никакой сессии у устройства ещё нет — за ней и пришли. Гвард, потребовавший
   * бы вход, отправил бы на `/login` и потерял код из фрагмента.
   *
   * Вне каркаса сканера, потому что каркас рисует шапку с именем вошедшего и
   * кнопкой выхода — а вошедшего ещё нет.
   *
   * ⚠️ Объявлен ДО `/scan`, хотя vue-router и так предпочтёт литерал параметру:
   * при равном счёте порядок решает, а `/scan/login` иначе уехал бы в
   * `scanner-event` с `eventId = "login"` и получил бы `VALIDATION_FAILED`.
   */
  {
    path: '/scan/login',
    name: 'scanner-ticket-login',
    component: () => import('@/views/scanner/ScannerTicketLoginView.vue'),
    meta: { public: true, title: 'Вход по коду' },
  },

  {
    path: '/scan',
    component: ScannerLayout,
    children: [
      {
        path: '',
        name: 'scanner',
        component: () => import('@/views/scanner/ScannerEventPickerView.vue'),
        meta: { title: 'Сканер' },
      },
      {
        path: ':eventId',
        name: 'scanner-event',
        component: () => import('@/views/scanner/ScannerView.vue'),
        props: true,
        meta: { title: 'Сканирование' },
      },
    ],
  },

  /**
   * 404 — публичный и **вне** shell'а: на неизвестный URL нельзя отвечать
   * редиректом на логин (это подсказывало бы, что за адресом что-то есть),
   * и нельзя требовать сайдбар с данными, которых может не быть.
   */
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/errors/NotFoundView.vue'),
    meta: { public: true, title: 'Страница не найдена' },
  },
]

export default routes
